import { createContext, useEffect, useState } from "react";
import { productsArray, getProductData } from "./components/productsData";

export const CartContext = createContext({
  items: [],
  getProductQuantity: () => {},
  addOneToCart: () => {},
  removeOneFromCart: () => {},
  deleteFromCart: () => {},
  getTotalCost: () => {},
  setCart: () => {},
  clearCart: () => {},
});

export function CartProvider({ children }) {
  const [cartProducts, setCartProducts] = useState([]);

  // Load cart from localStorage on initial render
  useEffect(() => {
    const storedCart = JSON.parse(localStorage.getItem("cart"));
    if (storedCart) {
      setCartProducts(storedCart);
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cartProducts));
  }, [cartProducts]);

  // Get the quantity of a product in the cart
  function getProductQuantity(id) {
    return cartProducts.find((product) => product.id === id)?.quantity || 0;
  }

  // Add a product to the cart (unchanged)
  function addOneToCart(id, finalPrice, quantity = 1) {
    const existingProduct = cartProducts.find((product) => product.id === id);
    if (existingProduct) {
      // If the product already exists, update its quantity
      setCartProducts((prevCart) =>
        prevCart.map((product) =>
          product.id === id
            ? { ...product, quantity: product.quantity + quantity }
            : product
        )
      );
    } else {
      // If the product doesn't exist, add it to the cart
      const productToAdd = getProductData(id);
      setCartProducts((prevCart) => [
        ...prevCart,
        { ...productToAdd, quantity, finalPrice },
      ]);
    }
  }

  // Remove one quantity of a product from the cart
  function removeOneFromCart(id) {
    const quantity = getProductQuantity(id);
    if (quantity === 1) {
      deleteFromCart(id);
    } else {
      setCartProducts((prevCart) =>
        prevCart.map((product) =>
          product.id === id
            ? { ...product, quantity: product.quantity - 1 }
            : product
        )
      );
    }
  }

  // Delete a product from the cart
  function deleteFromCart(id) {
    setCartProducts((prevCart) =>
      prevCart.filter((currentProduct) => currentProduct.id !== id)
    );
  }

  // Calculate the total cost of the cart
  function getTotalCost() {
    return cartProducts.reduce(
      (total, cartItem) => total + cartItem.finalPrice * cartItem.quantity,
      0
    );
  }

  // Set the cart to a new array of products
  const setCart = (newCart) => {
    setCartProducts(newCart);
  };

  // Clear the cart completely
  const clearCart = () => {
    setCartProducts([]);
    localStorage.removeItem("cart");
  };

  // Context value to be provided to consumers
  const contextValue = {
    items: cartProducts,
    getProductQuantity,
    addOneToCart,
    removeOneFromCart,
    deleteFromCart,
    getTotalCost,
    setCart,
    clearCart,
  };

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
}

export default CartProvider;
